import { useEffect, useMemo, useRef, useState } from 'react';
import { Check, X } from 'lucide-react';
import { api } from '../../services/apiService';
import { RubyLoader } from '../../components/RubyLoader';

// Linked packages for an academy module (SA-Packages, Board 3). A tenant sees
// the module only if one of these is in its `enabledPackages`; [] = everyone
// in the audience. Chip input + type-to-filter list -- custom control, no
// native <select>.

interface PackageOption { id: string; name: string; role?: 'tier' | 'addon'; status?: string }

// One fetch per page load, shared by every module card on the Curriculum tab.
let catalogPromise: Promise<PackageOption[]> | null = null;
const loadCatalog = () => {
  catalogPromise ??= api.packages.listVisible()
    .then((res) => (Array.isArray(res?.packages) ? res.packages : [])
      .filter((p: any) => p?.id)
      .map((p: any) => ({ id: String(p.id), name: String(p.name || p.id), role: p.role, status: p.status })))
    .catch((e) => { catalogPromise = null; throw e; });
  return catalogPromise;
};

export default function ModulePackagesField({
  value, onChange, tenants,
}: {
  value: string[];
  onChange(v: string[]): void;
  tenants?: Array<{ id: string; enabledPackages?: string[] }>;
}) {
  const [catalog, setCatalog] = useState<PackageOption[] | null>(null);
  const [loadError, setLoadError] = useState('');
  const [query, setQuery] = useState('');
  const [open, setOpen] = useState(false);
  const boxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    let live = true;
    loadCatalog()
      .then((c) => { if (live) setCatalog(c); })
      .catch((e: any) => { if (live) { setCatalog([]); setLoadError(e?.message || 'Could not load packages.'); } });
    return () => { live = false; };
  }, []);

  // Close the list on an outside click.
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      if (boxRef.current && !boxRef.current.contains(e.target as Node)) setOpen(false);
    };
    document.addEventListener('mousedown', onDown);
    return () => document.removeEventListener('mousedown', onDown);
  }, [open]);

  const byId = useMemo(() => new Map((catalog ?? []).map((p) => [p.id, p])), [catalog]);
  const nameOf = (id: string) => byId.get(id)?.name ?? id;

  // Archived packages are not offered, but an already-linked one still shows as a chip.
  const options = (catalog ?? [])
    .filter((p) => p.status !== 'archived')
    .filter((p) => !query.trim() || p.name.toLowerCase().includes(query.trim().toLowerCase()));

  const toggle = (id: string) =>
    onChange(value.includes(id) ? value.filter((x) => x !== id) : [...value, id]);

  // `enabledPackages` comes back on every tenant row that has packages (GET
  // /tenants returns whole rows). If NO tenant carries the field at all, the
  // list is not telling us entitlements -- show "—", never a misleading 0.
  const entitlementsKnown = !!tenants && (tenants.length === 0 || tenants.some((t) => Array.isArray(t.enabledPackages)));
  const reach = tenants === undefined || (value.length > 0 && !entitlementsKnown)
    ? null
    : value.length === 0
      ? tenants.length
      : tenants.filter((t) => (t.enabledPackages ?? []).some((p) => value.includes(p))).length;

  const reachCopy = value.length === 0
    ? 'No packages linked — every tenant in the audience.'
    : `Tenants with ${value.map(nameOf).join(' or ')} approved. Computed live from entitlements.`;

  return (
    <div className="flex flex-col gap-3">
      <div ref={boxRef} className="flex flex-col gap-2 min-w-0">
        <span className="text-[12.5px] font-semibold text-gray-700">
          Linked packages — tenants see this module only if one of these is approved
        </span>
        <div
          className={`min-h-[48px] rounded-[10px] border bg-white px-2.5 py-1.5 flex flex-wrap items-center gap-1.5 ${
            open ? 'border-[#DC143C] ring-[3px] ring-[#DC143C]/15' : 'border-gray-300'
          }`}
          onClick={() => setOpen(true)}
        >
          {value.map((id) => (
            <span key={id} className="inline-flex items-center gap-1 rounded-lg bg-[#FFF1F3] text-[#9F1239] text-[12.5px] font-semibold pl-2.5 pr-1">
              {nameOf(id)}
              <button
                type="button"
                onClick={(e) => { e.stopPropagation(); toggle(id); }}
                aria-label={`Remove ${nameOf(id)}`}
                // 44x44 hit area; negative margins keep the chip itself compact.
                className="group/x w-11 h-11 -my-2.5 -mr-1.5 inline-flex items-center justify-center"
              >
                <span className="w-6 h-6 inline-flex items-center justify-center rounded-md group-hover/x:bg-[#DC143C]/10">
                  <X className="w-3.5 h-3.5" />
                </span>
              </button>
            </span>
          ))}
          <input
            value={query}
            onChange={(e) => { setQuery(e.target.value); setOpen(true); }}
            onFocus={() => setOpen(true)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') { setOpen(false); (e.target as HTMLInputElement).blur(); }
              if (e.key === 'Enter' && options.length === 1) { e.preventDefault(); toggle(options[0].id); setQuery(''); }
              if (e.key === 'Backspace' && !query && value.length) toggle(value[value.length - 1]);
            }}
            placeholder={value.length ? '' : 'Type to add a package…'}
            aria-label="Add a package"
            className="flex-1 min-w-[120px] min-h-[36px] bg-transparent text-[13px] text-gray-900 placeholder:text-gray-400 outline-none"
          />
        </div>

        {open && (
          <div className="rounded-xl border border-gray-200 bg-white shadow-[0_12px_28px_rgba(0,0,0,0.1)] overflow-hidden max-h-[264px] overflow-y-auto" role="listbox" aria-multiselectable="true">
            {catalog === null && <RubyLoader variant="section" size={28} label="Loading packages…" />}
            {catalog !== null && options.length === 0 && (
              <p className="px-3.5 py-3 text-[13px] text-gray-500">{loadError || 'No package matches.'}</p>
            )}
            {options.map((p) => {
              const on = value.includes(p.id);
              return (
                <button
                  key={p.id}
                  type="button"
                  role="option"
                  aria-selected={on}
                  onClick={() => toggle(p.id)}
                  className="w-full flex items-center gap-2.5 px-3.5 min-h-[44px] border-b border-gray-100 last:border-b-0 text-left text-[13.5px] hover:bg-gray-50"
                >
                  <span className={`w-5 h-5 rounded-md flex items-center justify-center flex-shrink-0 ${
                    on ? 'bg-[#DC143C] text-white' : 'border-[1.5px] border-gray-300'
                  }`}>
                    {on && <Check className="w-3.5 h-3.5" strokeWidth={3} />}
                  </span>
                  <span className="font-medium text-gray-900 truncate">{p.name}</span>
                  <span className="ml-auto text-xs text-gray-500">{p.role === 'addon' ? 'add-on' : 'package'}</span>
                </button>
              );
            })}
          </div>
        )}
        <span className="text-xs text-gray-500">
          No packages = every tenant in the audience sees it. Chapters inherit the module’s packages.
        </span>
      </div>

      <div className="rounded-[14px] bg-gray-50 p-4 flex flex-col gap-1.5">
        <span className="text-[13px] font-bold text-gray-900">Who will see it</span>
        <span className="text-[28px] leading-tight font-bold text-[#DC143C]">
          {reach === null ? '—' : `${reach} tenant${reach === 1 ? '' : 's'}`}
        </span>
        <span className="text-xs text-gray-500 leading-relaxed">
          {reach === null
            ? (tenants === undefined ? 'Tenant list unavailable.' : 'Tenant entitlements unavailable.')
            : reachCopy}
        </span>
      </div>
    </div>
  );
}
